import { ChangeDetectionStrategy, Component, DestroyRef, Injector, TemplateRef, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { SafeHtml } from '@angular/platform-browser';
import { MarkdownComponent } from 'ngx-markdown';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdGateInfoContext } from '../hd-gate-info-context.model';
import {
  HdGateInfoRequestedContext,
  isHdGateInfoRequestedContext,
} from '../hd-gate-info-requested-context.model';
import { HD_GATE_INFO_PROVIDER } from '../hd-gate-info-provider';
import { HD_GATE_CATEGORIES, HdGateCategory } from '../../hd-gate-categories';
import {
  formatHdAbsoluteDegrees,
  formatHdZodiacDegrees,
  formatHdZodiacPosition,
  hdAbsoluteDegreesFormat,
} from './hd-zodiac-position.util';
import {
  formatHdGateActivationDescriptors,
  HdGateActivationDescriptor,
} from '../hd-gate-info-requested-labels.util';
import { HdPlanetInfoService, HdPlanetInfluenceTranslations } from '../../hd-planet-info.service';
import {
  HD_CHIRON_DISPLAY_CONFIG,
  HdChartPointKey,
  getHdAstroPointDisplayConfig,
  getHdPlanetDisplayConfig,
  getHdPlanetDisplayConfigByPlanetId,
} from '../../planet-grid/hd-planet-display-config';
import { HdBodygraphPlanetaryActivation, HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdChironSide, hdChironNatalRole } from '../../hd-chiron.models';
import {
  HD_ZODIAC_ASTRONOMICON_GLYPHS,
  hdZodiacPositionFromLongitude,
} from '../../hd-gate-coordinates';
import { HdGateInfoService } from '../../hd-gate-info.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../hd-content-language.service';
import { HdGateGraphPanelService } from '../../gate-graph/hd-gate-graph-panel.service';
import { HdPlanetInfoPanelService } from '../../planet-info/hd-planet-info-panel.service';
import { HdEmotionalWaveRowComponent } from '../../../emotional-waves/hd-emotional-wave-row.component';
import {
  HdEmotionalWaveRef,
  hdDefinedEmotionalChannelKeys,
  hdEmotionalWaveForGate,
} from '../../../emotional-waves/hd-emotional-waves.metadata';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { HdChartSearchContextService } from '../../../library/hd-chart-search-context.service';
import { HdChartGateMatch, HdChartMatchesService } from '../../../library/hd-chart-matches.service';
import {
  HdChartGateMatchScored,
  HdChartGateSearchSort,
  arrangeHdChartGateMatches,
  hdGateActivationWeight,
} from '../../../library/hd-chart-gate-match-sort';
import { HdChartSettingsStore } from '../../chart-tabs/hd-chart-settings.store';
import {
  HD_CHART_MATCH_LIST_IDLE,
  HdChartMatchListComponent,
  HdChartMatchListState,
} from '../../../library/hd-chart-match-list.component';
import { HdChartMatchToggleComponent } from '../../../library/hd-chart-match-toggle.component';
import { HdGateActivationChipComponent } from '../../gate-activation-chip/hd-gate-activation-chip.component';
import { CommunityContentAccessService } from '../../../../community-content/community-content-access.service';
import { HdLineCompanionPanelService } from '../../../../community-content/line-companion/hd-line-companion-panel.service';
import { HdIChingAccessService } from '../../../../iching/hd-iching-access.service';
import { HdIChingPanelService } from '../../../../iching/panel/hd-iching-panel.service';
import { HdBodygraphIconKey, getHdGateIconLayout } from '../../hd-bodygraph-icons';
import { HdGateAttributeIconComponent } from '../../gate-attributes/hd-gate-attribute-icon.component';
import { HdGateLocksKeysIconComponent } from '../../gate-attributes/hd-gate-locks-keys-icon.component';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { FLOATING_PANEL_TOPBAR_LEFT } from '@shared/floating-panel/floating-panel-topbar-content.token';
import {
  HD_BODYGRAPH_CIRCUIT_THEME_ICONS,
  resolveHdGateFooterCircuits,
} from '../../hd-bodygraph-circuit-metadata';
import {
  parsePlanetInfluenceMarkers,
  HdPlanetInfluenceSegment,
} from '../../planet-grid/hd-planet-influence-markers.util';
import { HdGateLineComponent } from '../line/hd-gate-line.component';
import {
  buildHdGateLineViewModel,
  getHdHarmonicGatesOf,
  HdGateLineActivationSource,
  HdGateLineActivationOrigin,
  HdGateLineFixationCauseLabels,
  HdGateLineViewModel,
  withHdFearIconDetail,
} from '../line/hd-gate-line.view-model';
import { resolveHdFixingState } from '../../planet-grid/hd-planet-grid.utils';
import { HdActivePaletteService } from '../../hd-active-palette.service';

/** Treffer van de poortzoektocht zoals de lijst hem toont: gesorteerd, met score en opbouw-tooltip. */
type HdGateInfoChartSearchEntry = HdChartGateMatchScored<HdChartGateMatch> & { readonly scoreTooltip: string };

/** Lijn-filter: alle 6 lijnen, of enkel de lijnen die in de chart geactiveerd zijn. */
type HdGateLineFilter = 'all' | 'active';

interface CategorySectionViewModel {
  readonly category: HdGateCategory;
  readonly description: string;
}

interface CoordinatesViewModel {
  readonly start: string;
  readonly end: string;
}

/** Golfregel in de header (enkel emotionele poorten). */
interface EmotionalWaveRowViewModel {
  readonly wave: HdEmotionalWaveRef;
  readonly keynoteKey: string;
  /** Poort 6: de lijnen van haar activaties (de bron licht die golven uit). */
  readonly sourceLines: readonly number[];
}

/**
 * Per-activatie regel met optionele inline planeet-invloed-keynote
 * (situatie-specifiek a.d.h.v. {@link HdGateInfoRequestedContext.gateDefinitionState}).
 */
/**
 * Eén circuit-vermelding van de full dialog: naam (vertaald), circuit-kleur,
 * en optioneel theme-icoon + theme-label. Spiegelt de footer-rij van de
 * cheat-sheet kaart. Gebruikt op twee plekken: de circuit-rij in de header
 * (kleurblokje + naam + keynote + icoon) en de compactere topbar-projectie
 * in floating-modus (naam + icoon).
 */
interface FullDialogCircuitViewModel {
  readonly key: string;
  readonly name: string;
  readonly color: string;
  readonly themeIcon: string | undefined;
  readonly themeLabel: string | undefined;
}

/**
 * Center-vermelding rechtsboven in de header: het mini center-icoon (zelfde
 * shape-bron als de kaartjes in het basis-info-paneel) plus de vertaalde
 * center-naam. Geeft de poort-context ook wanneer de gebruiker niet vanuit
 * een center vertrekt (bv. een klik op een poortnummer in de transits).
 */
interface FullDialogCenterViewModel {
  readonly name: string;
  readonly iconHtml: SafeHtml;
}

interface ActivationInfluenceVariantViewModel {
  /** Veldnaam binnen {@link HdPlanetInfluenceTranslations} (track-key). */
  readonly key: string;
  /** Vertaald label van de variant (uit `humanDesign.planetInfluence.<key>`). */
  readonly label: string;
  readonly segments: readonly HdPlanetInfluenceSegment[];
}

/**
 * Detail-regel onder een activatie-headline: de volledige activatie-keten
 * (poort.lijn.kleur.toon.base) plus de exacte zodiakpositie van de planeet.
 * De positie is afgeleid van de absolute ecliptische lengte
 * ({@link HdBodygraphPlanetaryActivation.longitude}, graden [0, 360)) —
 * omgerekend naar dd°mm'ss" binnen het teken, met Astronomicon-glyph.
 */
interface ActivationDetailViewModel {
  /** "39.1.4.5.2" — leeg wanneer kleur/toon/base voor deze bron ontbreken. */
  readonly chain: string;
  readonly position: {
    readonly degreesText: string;
    readonly glyph: string;
    readonly signLabel: string;
    /**
     * Absolute lengte 0-360° met 4 decimalen (bv. "129,5000°"), dezelfde waarde als de
     * Graden-weergave van de ephemeris-pagina: zo vergelijk je je chart rechtstreeks met
     * de dagposities (solar return, design-moment).
     */
    readonly absoluteText: string;
  } | null;
}

interface ActivationLineViewModel {
  readonly key: string;
  readonly glyph: string;
  readonly side: HdBodygraphSide | null;
  /** Bv. `"Personality Neptune"` — side + planeetnaam, zonder positie of subtitle. */
  readonly headline: string;
  /** Poort waarop deze activatie staat (gebruikt voor de `{gate}.{line}`-suffix). */
  readonly gate: number | null;
  /** Lijn waarop deze activatie staat (gebruikt voor het bold-dusk `.{line}`-stukje). */
  readonly line: number | null;
  /** Vertaalde planet-subtitle (uit de `humanDesign/planets`-scope, veld `subtitle`). */
  readonly subtitle: string;
  /** Planeet (of astrologisch punt) van de regel: drijft het info-icoon achter de basis-keynote (planeet-info-paneel). */
  readonly planetKey: HdChartPointKey | null;
  readonly prefixText: string | null;
  /** Keten + zodiakpositie; `null` zonder positie-data (bv. chartloze popup). */
  readonly detail: ActivationDetailViewModel | null;
  /**
   * Informatieve regel (Chiron): geen activatie en dus geen invloed-keynote;
   * deze notitie zegt dat. `null` voor gewone activaties.
   */
  readonly informativeNote: string | null;
  readonly influenceSegments: readonly HdPlanetInfluenceSegment[];
  /**
   * Alle niet-lege invloed-varianten met label — enkel gevuld wanneer er géén
   * chart-context is ({@link HdGateInfoRequestedContext.gateDefinitionState}
   * ontbreekt, bv. gate-info vanuit de transit-popup) en de situatie-specifieke
   * keuze dus niet gemaakt kan worden. Leeg in normale chart-context.
   */
  readonly influenceVariants: readonly ActivationInfluenceVariantViewModel[];
  readonly origin: HdGateLineActivationOrigin;
}

/**
 * Velden van {@link HdPlanetInfluenceTranslations} die in een chartloze context
 * als los gelabelde variant onder elkaar getoond worden, in weergavevolgorde.
 */
const HD_PLANET_INFLUENCE_VARIANT_KEYS = [
  'essence',
  'ravePsychology',
  'phs',
  'inDefinedChannel',
  'inDefinedCenter',
  'inUndefinedCenter',
  'influenceOn',
] as const satisfies readonly (keyof HdPlanetInfluenceTranslations)[];

/**
 * Lange detail-weergave van een poort: rendert alle uitgebreide velden (summary,
 * evolutionary, coördinaten, praktische implicaties, reflectievragen, categorie-info,
 * en per lijn: summary + exaltation/detriment met keynoteBlack/keynoteWhite/custom).
 *
 * Bevat **ook** de planeet-activatie-/invloed-blokken (verhuisd uit de vroegere
 * `requested`-variant): per activatie een regel met de situatie-specifieke
 * planeet-invloed-keynote inline, en fallback-regels wanneer er geen activatie is.
 * Deze blokken verschijnen enkel wanneer een rijke {@link HdGateInfoRequestedContext}
 * wordt aangeleverd (de factory-aanroep met enkel `gate`+`activations` laat ze leeg).
 *
 * Alle secties zijn empty-guarded — een leeg veld rendert geen container.
 */
@Component({
  selector: 'hd-gate-info-full',
  templateUrl: './hd-gate-info-full.component.html',
  styleUrl: './hd-gate-info-full.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MarkdownComponent,
    SharedModule,
    ESegmentedToggleComponent,
    TranslocoModule,
    HdGateLineComponent,
    HdChartMatchListComponent,
    HdChartMatchToggleComponent,
    HdGateActivationChipComponent,
    HdEmotionalWaveRowComponent,
    HdGateAttributeIconComponent,
    HdGateLocksKeysIconComponent,
  ],
})
export class HdGateInfoFullComponent {
  private readonly provider = inject(HD_GATE_INFO_PROVIDER);
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly centerColors = inject(HdActivePaletteService);
  /**
   * Dock-render-zone wordt geïnjecteerd door de dock-host wanneer deze variant
   * in een gedockte tab gemount is. `null` → de variant draait als floating
   * dialog en mag (in `'full'`-mode) compactere maten gebruiken — zie de
   * `.hd-gate-info-full--floating`-stijlregels.
   */
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });
  protected readonly isFloating = this.dockRenderZone == null;

  /**
   * Schrijfbaar signaal voor de topbar-left slot van de floating-panel host
   * (links bovenaan op gelijke hoogte als de pin/close-knoppen). Wordt enkel
   * gevuld in floating-modus — een gedockt panel heeft geen host-topbar.
   */
  private readonly topbarLeftSignal = inject(FLOATING_PANEL_TOPBAR_LEFT, { optional: true });
  private readonly circuitTopbarTemplate = viewChild<TemplateRef<unknown>>('circuitTopbar');

  /**
   * Accepteert zowel de basis-context (factory-aanroep `variant="full"`) als de
   * rijke router-context (floating panel). De activatie-/invloed-blokken worden
   * enkel gevuld bij een rijke context.
   */
  readonly context = input.required<HdGateInfoContext | HdGateInfoRequestedContext>();

  /**
   * Tijdelijke sectie-styling variant (A/B/C). Wordt door de
   * {@link HdGateInfoRequestedComponent}-wrapper bestuurd via een toggle-
   * knop in de host-actions-strook. Default `'c'` (amber horizontale lijn
   * boven sectietitels + amber-gekleurde titel).
   */
  readonly sectionVariant = input<'a' | 'b' | 'c'>('c');

  /** Vraagt de router-variant om terug naar de compacte weergave te wisselen. */
  readonly collapseRequested = output<void>();

  protected readonly gate = computed(() => this.context().gate);
  protected readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  protected readonly gateInfo = computed(() => this.provider.gateInfoIn(this.gate(), this.lang()));
  protected readonly activations = computed(() => this.context().activations ?? []);

  /**
   * Radiale poort-kaart: knop onder de center-vermelding die deze poort als
   * fractal-achtige boom opent (poort → lijnen → kleuren → tonen → basissen).
   * Zie `docs/superpowers/specs/2026-07-30-hd-gate-radial-graph-design.md`.
   */
  private readonly gateGraphPanel = inject(HdGateGraphPanelService);

  protected readonly graphButtonLabels = computed(() => {
    const lang = this.lang();
    const gate = this.gateInfo().gate;
    return {
      tooltip: this.transloco.translate('humanDesign.gateGraph.openTooltip', undefined, lang),
      aria: this.transloco.translate('humanDesign.gateGraph.openAria', { gate }, lang),
    };
  });

  protected openGateGraph(event: MouseEvent): void {
    this.gateGraphPanel.open(
      this.gateInfo().gate,
      this.activations(),
      { x: event.clientX, y: event.clientY },
    );
  }

  private readonly communityAccess = inject(CommunityContentAccessService);
  private readonly lineCompanionPanel = inject(HdLineCompanionPanelService);

  /** Line Companion-icoon achter de center-vermelding (community-content: feature + opt-in). */
  protected readonly lineCompanionVisible = computed(() => this.communityAccess.lineCompanionVisible());

  protected readonly lineCompanionLabel = computed(() =>
    this.transloco.translate(
      'humanDesign.chart.lineCompanion.gateTooltip',
      { gate: this.gateInfo().gate },
      this.lang(),
    ),
  );

  protected openLineCompanion(event: MouseEvent): void {
    this.lineCompanionPanel.openGate(this.gateInfo().gate, {
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);

  /** Classic I Ching-icoon achter de center-vermelding (feature + setting). */
  protected readonly classicIChingVisible = this.ichingAccess.classicVisible;

  protected readonly classicIChingLabel = computed(() =>
    this.transloco.translate(
      'humanDesign.chart.classicIChing.gateTooltip',
      { gate: this.gateInfo().gate },
      this.lang(),
    ),
  );

  protected openClassicIChing(event: MouseEvent): void {
    this.ichingPanel.openHexagram(this.gateInfo().gate, {
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  private readonly planetInfoPanel = inject(HdPlanetInfoPanelService);

  /** Info-icoon achter de basis-keynote van de activerende planeet: opent het planeet-info-paneel. */
  protected openPlanetInfo(planetKey: HdChartPointKey, event: MouseEvent): void {
    event.stopPropagation();
    this.planetInfoPanel.openForPlanet(planetKey, { x: event.clientX, y: event.clientY });
  }

  /**
   * Poort-attribuut-iconen (angst, liefde, geheugen, …) op poortniveau in de
   * header, onder het center-icoon en de radial-map-knop. Zelfde bron als de
   * per-lijn-iconen ({@link getHdGateIconLayout}); klikbaarheid en panelen
   * regelt {@link HdGateAttributeIconComponent}. De tooltip van een angst-icoon
   * krijgt de specifieke angst van de poort erbij.
   */
  protected readonly headerIconRows = computed<readonly (readonly {
    readonly key: HdBodygraphIconKey;
    readonly label: string;
  }[])[]>(() => {
    const gate = this.gateInfo().gate;
    const labels = this.gateInfoService.iconLabelsIn(this.lang());
    const fearDetail = this.gateInfoService.fearIconDetailIn(gate, this.lang());
    return getHdGateIconLayout(gate).map(row =>
      row.map(key => ({ key, label: withHdFearIconDetail(key, labels[key] ?? '', fearDetail) })),
    );
  });

  // ── Charts met deze poort (spec 2026-10-21-poortactivatie-zoektochten §4.4) ──

  private readonly chartSearchContext = inject(HdChartSearchContextService);
  /**
   * De matches-service (en via de bibliotheek de HTTP-laag) pas resolven bij de
   * eerste zoekopdracht: poort-info draait ook waar niet gezocht kan worden
   * (cheat sheets, gedeelde chart, specs zonder HttpClient).
   */
  private readonly injector = inject(Injector);

  /** Zoekicoon enkel op de chart-pagina: die opent de treffers in een nieuwe tab. */
  protected readonly chartSearchAvailable = computed(() => this.chartSearchContext.chartOpener() != null);
  protected readonly chartSearchOpen = signal(false);
  protected readonly chartSearchState = signal<HdChartMatchListState<HdChartGateMatch>>(HD_CHART_MATCH_LIST_IDLE);
  /** Volgnummer van de laatste zoekopdracht: een trager antwoord van een vorige poort telt niet. */
  private chartSearchRequest = 0;

  protected readonly chartSearchLabels = computed(() => {
    const lang = this.lang();
    const gate = this.gate();
    const t = (key: string) => this.transloco.translate<string>(`humanDesign.gateInfo.chartSearch.${key}`, { gate }, lang);
    const related = this.chartSearchContext.matchScope() === 'related';
    return {
      tooltip: t('tooltip'),
      title: t(related ? 'titleRelated' : 'title'),
      loading: t('loading'),
      empty: t('empty'),
      error: t('error'),
      sortLabel: t('sortLabel'),
    };
  });

  // ── Sortering van de treffers (spec 2026-10-30-poortzoektocht-gewichtssortering) ──

  private readonly settings = inject(HdChartSettingsStore);
  /** Gebruikersinstelling: zwaarste activaties eerst (gewicht) of alfabetisch. */
  protected readonly chartSearchSort = this.settings.gateSearchSort;

  protected readonly chartSearchSortOptions = computed<readonly ESegmentedToggleOption<HdChartGateSearchSort>[]>(() => {
    const lang = this.lang();
    const t = (key: string) => this.transloco.translate<string>(`humanDesign.gateInfo.chartSearch.${key}`, undefined, lang);
    return [
      { value: 'weight', label: t('sortWeight'), title: t('sortWeightTooltip') },
      { value: 'name', label: t('sortName'), title: t('sortNameTooltip') },
    ];
  });

  /**
   * Weergave van de treffers: gesorteerd volgens de instelling, met per treffer de
   * score en haar opbouw ("Zon design 12 + Maan personality 6") als tooltip.
   * Werkt op de geladen treffers, zonder nieuwe zoektocht.
   */
  protected readonly chartSearchListState = computed<HdChartMatchListState<HdGateInfoChartSearchEntry>>(() => {
    const state = this.chartSearchState();
    if (state.status !== 'loaded') return { status: state.status, items: [] };
    const lang = this.lang();
    const planetNames = this.planetInfoService.planetTranslationsAllIn(lang);
    const sideLabel = (side: string | null | undefined) => this.transloco.translate<string>(
      `humanDesign.properties.${side === 'design' ? 'tabDesign' : 'tabPersonality'}`, undefined, lang,
    ).toLocaleLowerCase();
    const items = arrangeHdChartGateMatches(state.items, this.chartSearchSort()).map(match => {
      const parts = match.activations.map(activation => {
        const config = activation.planetId == null ? undefined : getHdPlanetDisplayConfigByPlanetId(activation.planetId);
        const planet = config ? planetNames[config.key]?.name ?? config.label : '';
        return `${planet} ${sideLabel(activation.side)} ${hdGateActivationWeight(activation.planetId)}`.trim();
      });
      const scoreTooltip = this.transloco.translate<string>(
        'humanDesign.gateInfo.chartSearch.scoreTooltip',
        { score: match.score, parts: parts.join(' + ') },
        lang,
      );
      return { ...match, scoreTooltip };
    });
    return { status: 'loaded', items };
  });

  protected setChartSearchSort(sort: HdChartGateSearchSort): void {
    this.settings.gateSearchSort.set(sort);
  }

  /**
   * Andere poort of scope: de vorige treffers kloppen niet meer. Staat de lijst
   * open, dan meteen opnieuw zoeken (de poort-info wisselt van poort zonder te
   * sluiten bij "full-docked-keep").
   */
  private readonly resetChartSearch = effect(() => {
    this.gate();
    this.chartSearchContext.matchScope();
    untracked(() => {
      this.chartSearchRequest++;
      this.chartSearchState.set(HD_CHART_MATCH_LIST_IDLE);
      if (this.chartSearchOpen()) void this.loadChartSearch();
    });
  });

  /**
   * Een chartwissel klapt de lijst dicht i.p.v. opnieuw te zoeken: wie door zijn
   * charts klikt, start zo niet bij elke wissel een zoektocht (Steven 2026-10-21).
   */
  private readonly collapseOnChartChange = effect(() => {
    this.chartSearchContext.activeChartKey();
    untracked(() => {
      this.chartSearchRequest++;
      this.chartSearchOpen.set(false);
      this.chartSearchState.set(HD_CHART_MATCH_LIST_IDLE);
    });
  });

  protected toggleChartSearch(): void {
    const open = !this.chartSearchOpen();
    this.chartSearchOpen.set(open);
    if (open && this.chartSearchState().status === 'idle') void this.loadChartSearch();
  }

  protected openChartSearchMatch(item: PlatformChartListItemDto): void {
    if (item.id) this.chartSearchContext.chartOpener()?.(item.id);
  }

  private async loadChartSearch(): Promise<void> {
    const request = ++this.chartSearchRequest;
    this.chartSearchState.set({ status: 'loading', items: [] });
    try {
      const items = await this.injector.get(HdChartMatchesService).findGateMatches({
        gates: [this.gate()],
        match: 'all',
        scope: this.chartSearchContext.matchScope(),
        sourceChartId: this.chartSearchContext.relationSourceChartId(),
        excludeChartIds: this.chartSearchContext.excludeChartIds(),
      });
      if (request === this.chartSearchRequest) this.chartSearchState.set({ status: 'loaded', items });
    } catch {
      if (request === this.chartSearchRequest) this.chartSearchState.set({ status: 'error', items: [] });
    }
  }

  private readonly transitActivationIds = computed(() => new Set(
    this.requestedContext()?.transitActivations?.map(activation => activation.id) ?? [],
  ));

  /** Cycle chart: bron-tinten i.p.v. integraal groen voor de transitlaag. */
  protected readonly isCycleTransitStyle = computed(
    () => this.requestedContext()?.transitStyle === 'cycle',
  );

  /** Composite chart: chart 1 (de basislaag) kleurt natal-blauw i.p.v. rood/zwart. */
  protected readonly isCompositeBaseStyle = computed(
    () => this.requestedContext()?.baseStyle === 'composite',
  );
  protected readonly isTransitOnlyGate = computed(() => {
    const transitIds = this.transitActivationIds();
    const activations = this.activations();
    return activations.some(activation => transitIds.has(activation.id))
      && !activations.some(activation => !transitIds.has(activation.id));
  });

  /**
   * View-model per lijn (1..6) voor het gedeelde {@link HdGateLineComponent}.
   * Volgt de content-taal-override (planeet-namen). Vervangt de vroegere inline
   * per-lijn markup (keynote-header + exaltation/detriment) door het lijn-component.
   */
  protected readonly lineViewModels = computed<readonly HdGateLineViewModel[]>(() => {
    const gate = this.gate();
    const lang = this.lang();
    const planetTranslations = this.planetInfoService.planetTranslationsAllIn(lang);
    const iconLabels = this.gateInfoService.iconLabelsIn(lang);
    const voice = this.gateInfo().translations.voice;
    const gateActivations = this.activations().map<HdGateLineActivationSource>(activation => ({
      planetId: activation.planetId,
      planet: activation.planet,
      side: activation.side,
      gate: activation.gate,
      line: activation.line,
      fixing: resolveHdFixingState(activation),
      origin: this.transitActivationIds().has(activation.id) ? 'transit' : 'chart',
    }));
    const harmonicGatesActivations = this.harmonicGatesActivations();
    const noActivationLabel = this.translateWithLang(
      'humanDesign.gateInfo.lineFilter.noActivation',
      lang,
    );
    const chiron = this.chiron();
    const astroPoint = this.astroPoint();
    const informativePositions = [
      ...(chiron ? [{ planetKey: 'chiron' as const, side: chiron.side, line: chiron.line }] : []),
      ...(astroPoint ? [{ planetKey: astroPoint.key, side: astroPoint.side, line: astroPoint.line }] : []),
    ];
    const informativeLabel = this.translateWithLang('humanDesign.gateInfo.chironInformative', lang);
    const fixationCauseLabels: HdGateLineFixationCauseLabels = {
      prefix: this.translateWithLang('humanDesign.gateInfo.fixationCause.prefix', lang),
      inThisGate: (planetName: string) => planetName,
      // Geef de placeholders direct aan Transloco mee: een post-translate
      // `.replace('{{planet}}', ...)` werkt niet omdat Transloco de `{{...}}`-
      // tokens al heeft geïnterpoleerd (en bij ontbrekende params met een lege
      // string vervangen — waardoor we eerder "fixatie door  in poort " zagen).
      inHarmonicGate: (planetName: string, harmonicGate: number) =>
        this.transloco.translate(
          'humanDesign.gateInfo.fixationCause.inHarmonicGate',
          { planet: planetName, gate: harmonicGate },
          lang,
        ),
      transitSource: (causeLabel: string) => this.transloco.translate(
        'humanDesign.gateInfo.fixationCause.transitSource',
        { cause: causeLabel },
        lang,
      ),
    };
    return this.gateInfo().translations.lines.map((line, index) =>
      buildHdGateLineViewModel({
        gate,
        lineNumber: index + 1,
        line,
        planetTranslations,
        iconLabels,
        voice,
        fearDetail: this.gateInfoService.fearIconDetailIn(gate, lang),
        gateActivations,
        harmonicGatesActivations,
        noActivationLabel,
        fixationCauseLabels,
        informativePositions,
        informativeLabel,
      }),
    );
  });

  /**
   * Activaties van alle harmonische poorten van déze poort, in een formaat dat
   * de lijn-builder kan consumeren. Wordt enkel ingevuld wanneer de rijke
   * router-context ook `allActivations` (alle chart-activaties) doorgeeft.
   */
  private readonly harmonicGatesActivations = computed<readonly HdGateLineActivationSource[]>(() => {
    const ctx = this.requestedContext();
    if (!ctx) return [];
    const harmonics = new Set(getHdHarmonicGatesOf(this.gate()));
    if (harmonics.size === 0) return [];
    const chartSources = (ctx.allActivations ?? [])
      .filter(activation => harmonics.has(activation.gate))
      .map<HdGateLineActivationSource>(activation => ({
        planetId: activation.planetId,
        planet: activation.planet,
        side: activation.side,
        gate: activation.gate,
        line: activation.line,
        fixing: resolveHdFixingState(activation),
        origin: 'chart',
      }));
    const transitSources = (ctx.transitActivations ?? [])
      .filter(activation => harmonics.has(activation.gate))
      .map<HdGateLineActivationSource>(activation => ({
        planetId: activation.planetId,
        planet: activation.planet,
        side: activation.side,
        gate: activation.gate,
        line: activation.line,
        fixing: resolveHdFixingState(activation),
        origin: 'transit',
      }));
    return [...chartSources, ...transitSources];
  });

  /**
   * Chiron-positie van deze persoon op déze poort (enkel bij een Chiron-klik).
   * Geen activatie: zie {@link HdGateInfoRequestedContext.chiron}.
   */
  protected readonly chiron = computed(() => {
    const chiron = this.requestedContext()?.chiron;
    return chiron != null && chiron.gate === this.gate() ? chiron : null;
  });

  /**
   * Heeft déze poort lijnen om op te focussen: activaties in de chart, of een
   * Chiron-positie (die zelf géén activatie is)? Zo niet, dan tonen we de
   * lijn-filter niet en blijven alle lijnen zichtbaar.
   */
  protected readonly gateHasActivations = computed<boolean>(() =>
    this.activations().some(a => a.side === 'personality' || a.side === 'design')
    || this.chiron() != null
    || this.astroPoint() != null,
  );

  /**
   * Astrologisch punt (AC, DC, MC, IC, Lilith) van deze persoon op déze poort, enkel bij een klik in
   * de astro-rij. Geen activatie: zie {@link HdGateInfoRequestedContext.astroPoint}.
   */
  protected readonly astroPoint = computed(() => {
    const astroPoint = this.requestedContext()?.astroPoint;
    return astroPoint != null && astroPoint.gate === this.gate() ? astroPoint : null;
  });

  /** Door de gebruiker gekozen filter; `null` → automatische default per chart. */
  private readonly lineFilterOverride = signal<HdGateLineFilter | null>(null);

  /**
   * Effectief filter: gekozen override, anders automatisch — `active` wanneer de
   * poort activaties heeft (toon enkel geactiveerde lijnen), anders `all`.
   */
  protected readonly lineFilter = computed<HdGateLineFilter>(
    () => this.lineFilterOverride() ?? (this.gateHasActivations() ? 'active' : 'all'),
  );

  /** Lijnen na toepassing van het filter (alle lijnen, of enkel geactiveerde). */
  protected readonly displayedLineViewModels = computed<readonly HdGateLineViewModel[]>(() => {
    const lines = this.lineViewModels();
    // 'active' toont ook de lijn van een Chiron-positie, al is die geen activatie.
    return this.lineFilter() === 'active'
      ? lines.filter(line => line.activations.length > 0 || (line.informativePlanets?.length ?? 0) > 0)
      : lines;
  });

  /** Knoppen van de lijnfilter (gedeelde e-segmented-toggle). */
  protected readonly lineFilterOptions = computed<readonly ESegmentedToggleOption<HdGateLineFilter>[]>(() => {
    const labels = this.labels();
    return [
      { value: 'active', label: labels.filterActive },
      { value: 'all', label: labels.filterAll },
    ];
  });

  protected setLineFilter(filter: HdGateLineFilter): void {
    this.lineFilterOverride.set(filter);
  }

  /** Rijke router-context wanneer beschikbaar (anders `null`). */
  private readonly requestedContext = computed<HdGateInfoRequestedContext | null>(() => {
    const ctx = this.context();
    return isHdGateInfoRequestedContext(ctx as { gate: number; kind?: string })
      ? (ctx as HdGateInfoRequestedContext)
      : null;
  });

  constructor() {
    effect(() => {
      // Laad de lazy categorie-scopes in de effectieve content-taal zodat
      // categorySections meteen vertaalde content kan tonen na een taalwissel
      // op de editor-pagina (en niet enkel bij globale app-taalwissels).
      this.provider.loadGateCategoryScopesForGateIn(this.gate(), this.lang());
    });

    // Projecteer de circuit-vermelding naar de topbar-left slot van de
    // floating-panel host — zo behoudt de poort-titel haar volle breedte
    // i.p.v. ruimte te delen met de circuit-info. Reset wanneer er geen
    // circuits zijn of het panel niet floating is.
    effect(() => {
      if (!this.topbarLeftSignal) return;
      const tpl = this.circuitTopbarTemplate();
      this.topbarLeftSignal.set(tpl ?? null);
    });

    // Bij destroy van déze variant (bv. mode-switch full→small) de slot
    // expliciet wissen — de TemplateRef hoort bij deze view en mag niet
    // door de host blijven gerenderd worden nadat de view is afgebroken.
    inject(DestroyRef).onDestroy(() => this.topbarLeftSignal?.set(null));
  }

  /** Fallback-regels (wanneer er geen activaties zijn). */
  protected readonly fallbackLines = computed<readonly string[]>(() => {
    const ctx = this.requestedContext();
    if (!ctx) return [];
    const lang = this.lang();
    const descriptors = formatHdGateActivationDescriptors(ctx);
    const fallback = descriptors.find(d => d.fallbackKind !== null);
    if (!fallback) return [];
    const key = fallback.fallbackKind === 'dummy'
      ? 'humanDesign.gateInfoRequested.dummyActiveViaEditor'
      : 'humanDesign.gateInfoRequested.noPlanetActivation';
    return [this.translateWithLang(key, lang)];
  });

  /**
   * View-model per activatie-regel: titel + (indien beschikbaar) gemarkeerde
   * tekst-segmenten van de actieve invloed-keynote. Headline, inline-prefix
   * en invloed-keynote volgen allemaal de content-taal-override.
   *
   * Variant-keuze per regel:
   *  - transit-activaties (just-now) gebruiken de definitie-staat van de
   *    volledige transit-chart (`transitGateDefinitionState`, natal + transit)
   *    wanneer de context die meelevert — een transit die een kanaal/center
   *    voltooit toont dan de 'defined'-variant;
   *  - natal-activaties en contexten zonder transit gebruiken de natal
   *    `gateDefinitionState` (ongewijzigd);
   *  - zonder enige definitie-staat (chartloze context, bv. vanuit de
   *    transit-popup) toont de regel ALLE niet-lege invloed-varianten met
   *    label onder elkaar (`influenceVariants`).
   */
  protected readonly activationViewModels = computed<readonly ActivationLineViewModel[]>(() => {
    const ctx = this.requestedContext();
    if (!ctx) return [];

    const lang = this.lang();
    const descriptors = formatHdGateActivationDescriptors(ctx);
    const transitIds = this.transitActivationIds();
    const influences = this.planetInfoService.planetInfluencesAllIn(lang);
    const planetTranslations = this.planetInfoService.planetTranslationsAllIn(lang);

    const activationLines = descriptors
      .filter(descriptor => descriptor.fallbackKind === null)
      .map<ActivationLineViewModel>(descriptor => {
        const origin: HdGateLineActivationOrigin =
          descriptor.activationId != null && transitIds.has(descriptor.activationId)
            ? 'transit'
            : 'chart';
        const definitionState = origin === 'transit'
          ? ctx.transitGateDefinitionState ?? ctx.gateDefinitionState
          : ctx.gateDefinitionState;
        const influence = descriptor.planetKey ? influences[descriptor.planetKey] : undefined;
        const keynoteText = influence && definitionState ? influence[definitionState] : '';
        const glyph = descriptor.planetKey ? getHdPlanetDisplayConfig(descriptor.planetKey).astronomiconChar : '';
        const subtitle = descriptor.planetKey ? planetTranslations[descriptor.planetKey]?.subtitle ?? '' : '';
        const activation = descriptor.activationId != null
          ? ctx.activations.find(candidate => candidate.id === descriptor.activationId)
          : undefined;
        return {
          key: descriptor.key,
          glyph,
          side: descriptor.side,
          headline: this.buildHeadline(descriptor, planetTranslations, lang),
          gate: descriptor.gate,
          line: descriptor.line,
          subtitle,
          planetKey: descriptor.planetKey,
          prefixText: keynoteText && definitionState
            ? this.translateWithLang(`humanDesign.planetInfluence.inlinePrefix.${definitionState}`, lang)
            : null,
          detail: this.buildActivationDetail(activation, lang),
          influenceSegments: keynoteText ? parsePlanetInfluenceMarkers(keynoteText) : [],
          influenceVariants: definitionState
            ? []
            : this.buildAllInfluenceVariants(influence, lang),
          origin,
          informativeNote: null,
        };
      });
    const informativeLines = [
      this.chironLineViewModel(planetTranslations, lang),
      this.astroPointLineViewModel(planetTranslations, lang),
    ].filter((line): line is ActivationLineViewModel => line != null);
    return [...activationLines, ...informativeLines];
  });

  /**
   * Uitzonderlijke regel voor Chiron: dezelfde planeet-regel als bij een
   * activatie (zijde + naam, ondertitel, planeet-info-icoon, keten en exacte
   * zodiakpositie), maar zonder invloed-keynote — Chiron is geen activatie en
   * heeft geen invloed-data. Een korte notitie zegt dat.
   */
  private chironLineViewModel(
    planetTranslations: ReturnType<HdPlanetInfoService['planetTranslationsAllIn']>,
    lang: string,
  ): ActivationLineViewModel | null {
    const chiron = this.chiron();
    if (chiron == null) return null;
    const sideLabel = this.translateWithLang(`humanDesign.bodygraphSide.${chiron.side}`, lang);
    const planetName = planetTranslations.chiron?.name ?? HD_CHIRON_DISPLAY_CONFIG.label;
    return {
      key: `chiron:${chiron.side}`,
      glyph: HD_CHIRON_DISPLAY_CONFIG.astronomiconChar,
      side: chiron.side,
      headline: [sideLabel, planetName].filter(part => part.length > 0).join(' '),
      gate: chiron.gate,
      line: chiron.line,
      subtitle: planetTranslations.chiron?.subtitle ?? '',
      planetKey: 'chiron',
      prefixText: null,
      detail: this.buildActivationDetail(chiron, lang),
      influenceSegments: [],
      influenceVariants: [],
      origin: 'chart',
      informativeNote: this.chironRoleNote(chiron.side, lang),
    };
  }

  /**
   * Notitie onder de Chiron-regel: de rol van deze positie in het wond-verhaal
   * (wond / werk / resolutie), gevolgd door de vermelding dat het geen activatie is.
   */
  private chironRoleNote(side: HdChironSide, lang: string): string {
    const role = this.requestedContext()?.chironRole ?? hdChironNatalRole(side);
    const roleText = this.translateWithLang(`humanDesign.gateInfo.chironRoles.${role}`, lang);
    const informative = this.translateWithLang('humanDesign.gateInfo.chironInformative', lang);
    return [roleText, informative].filter(part => part.length > 0).join('. ');
  }

  /**
   * Regel voor een astrologisch punt (AC, DC, MC, IC, Lilith), zelfde vorm als de Chiron-regel: de
   * basis-keynote (subtitle) en het planeet-info-icoon, maar geen invloed-keynote, want het punt is
   * geen activatie en heeft geen invloed-data.
   */
  private astroPointLineViewModel(
    planetTranslations: ReturnType<HdPlanetInfoService['planetTranslationsAllIn']>,
    lang: string,
  ): ActivationLineViewModel | null {
    const astroPoint = this.astroPoint();
    if (astroPoint == null) return null;
    const config = getHdAstroPointDisplayConfig(astroPoint.key);
    const sideLabel = this.translateWithLang(`humanDesign.bodygraphSide.${astroPoint.side}`, lang);
    const pointName = planetTranslations[astroPoint.key]?.name ?? config.label;
    return {
      key: `${astroPoint.key}:${astroPoint.side}`,
      glyph: config.astronomiconChar,
      side: astroPoint.side,
      headline: [sideLabel, pointName].filter(part => part.length > 0).join(' '),
      gate: astroPoint.gate,
      line: astroPoint.line,
      subtitle: planetTranslations[astroPoint.key]?.subtitle ?? '',
      planetKey: astroPoint.key,
      prefixText: null,
      detail: this.buildActivationDetail(astroPoint, lang),
      influenceSegments: [],
      influenceVariants: [],
      origin: 'chart',
      informativeNote: this.translateWithLang('humanDesign.gateInfo.chironInformative', lang),
    };
  }

  /**
   * Keten + zodiakpositie voor één activatie. De keten toont
   * poort.lijn.kleur.toon.base en verschijnt enkel wanneer kleur/toon/base
   * alle drie aanwezig zijn (> 0). Activaties zonder enige positie-data —
   * de chartloze transit-popup vult longitude én kleur/toon/base met 0 —
   * krijgen helemaal geen detail-regel.
   */
  private buildActivationDetail(
    activation: Pick<HdBodygraphPlanetaryActivation, 'gate' | 'line' | 'color' | 'tone' | 'base' | 'longitude'> | undefined,
    lang: string,
  ): ActivationDetailViewModel | null {
    if (!activation) {
      return null;
    }
    const hasSubline = activation.color > 0 && activation.tone > 0 && activation.base > 0;
    if (!hasSubline && activation.longitude === 0) {
      return null;
    }
    const position = hdZodiacPositionFromLongitude(activation.longitude);
    return {
      chain: hasSubline
        ? `${activation.gate}.${activation.line}.${activation.color}.${activation.tone}.${activation.base}`
        : '',
      position: {
        degreesText: formatHdZodiacDegrees(position),
        glyph: HD_ZODIAC_ASTRONOMICON_GLYPHS[position.zodiacSign],
        signLabel: this.provider.zodiacLabelIn(position.zodiacSign, lang),
        absoluteText: formatHdAbsoluteDegrees(activation.longitude, hdAbsoluteDegreesFormat(lang)),
      },
    };
  }

  /**
   * Alle niet-lege invloed-varianten van een planeet met hun vertaalde label.
   * Enkel gebruikt in een chartloze context (geen `gateDefinitionState`), zodat
   * de gebruiker alle mogelijke invloeden kan lezen. Lege/null-velden (en
   * planeten zonder invloed-data, zoals Chiron) worden overgeslagen.
   */
  private buildAllInfluenceVariants(
    influence: HdPlanetInfluenceTranslations | undefined,
    lang: string,
  ): readonly ActivationInfluenceVariantViewModel[] {
    if (!influence) return [];
    return HD_PLANET_INFLUENCE_VARIANT_KEYS
      .map(key => ({
        key,
        label: this.translateWithLang(`humanDesign.planetInfluence.${key}`, lang),
        segments: parsePlanetInfluenceMarkers(influence[key]),
      }))
      .filter(variant => variant.segments.length > 0);
  }

  /**
   * Center waarin deze poort ligt (icoon + vertaalde naam), rechtsboven in de
   * header. Het icoon wordt met zijn chart-kleur gebakken — dezelfde aanpak
   * als {@link HdCenterMiniCardComponent}: een via `[innerHTML]` geïnjecteerde
   * SVG krijgt de style-encapsulatie-attributen niet, dus scoped CSS-regels
   * bereiken hem nooit.
   */
  protected readonly centerBadge = computed<FullDialogCenterViewModel>(() => {
    const center = this.gateInfo().center;
    return {
      name: this.gateInfoService.centerLabelsIn(this.lang())[center] ?? center,
      iconHtml: this.centerColors.iconHtml(center),
    };
  });

  /**
   * Circuit-vermelding(en) voor de huidige poort — spiegel van de cheat-sheet
   * footer. Voedt zowel de circuit-rij in de header (onder de korte
   * iching-zin) als de topbar-projectie in floating-modus. Volgt de
   * content-taal voor zowel de circuit-naam als het theme-label.
   */
  protected readonly footerCircuits = computed<readonly FullDialogCircuitViewModel[]>(() => {
    const lang = this.lang();
    const circuitNames = this.gateInfoService.circuitNamesIn(lang);
    const themeLabels = this.gateInfoService.circuitThemeLabelsIn(lang);
    const circuits = resolveHdGateFooterCircuits(this.gateInfo().circuits);
    const useShortName = circuits.length > 1;
    return circuits.map(circuit => {
      const labels = circuitNames[circuit.key];
      const fullName = labels?.name ?? circuit.key;
      const shortName = labels?.shortName ?? fullName;
      const preferShort = useShortName || circuit.key === 'collectief-abstract';
      return {
        key: circuit.key,
        name: preferShort ? shortName : fullName,
        color: circuit.color,
        themeIcon: circuit.theme ? HD_BODYGRAPH_CIRCUIT_THEME_ICONS[circuit.theme] : undefined,
        themeLabel: circuit.theme ? themeLabels[circuit.theme] : undefined,
      };
    });
  });

  /**
   * Overige UI-labels (collapse-knop, lijn-filter) — verzameld op één plek
   * zodat ze allemaal de actieve content-taal volgen. De `| transloco`-pipe
   * in de template kijkt naar de globale app-taal en zou hier dus de override
   * van de editor-pagina niet honoreren.
   */
  protected readonly labels = computed(() => {
    const lang = this.lang();
    const t = (key: string) => this.translateWithLang(key, lang);
    return {
      collapse: t('humanDesign.gateInfo.collapse'),
      filterActive: t('humanDesign.gateInfo.lineFilter.active'),
      filterAll: t('humanDesign.gateInfo.lineFilter.all'),
      melancholy: t('humanDesign.gateInfo.melancholyLabel'),
    };
  });

  /**
   * Melancholiethema van de poort (enkel individuele-circuit-poorten hebben
   * er één) — getoond als regel onder de circuit-vermelding in de header.
   * De categorie-scope wordt door het constructor-effect lazy geladen;
   * {@link HdGateInfoProvider.gateCategoryInfoIn} hervalideert na load.
   */
  protected readonly melancholyTheme = computed<string>(
    () => this.provider.gateCategoryInfoIn(this.gate(), 'melancholy', this.lang())?.keynote ?? '',
  );

  /**
   * Emotionele golf van de poort (enkel de zeven Solar Plexus-poorten): golfje,
   * golfnaam en de keynote van de poort, als regel onder de circuit-vermelding.
   * Poort 6 is de bron van alle drie golven; de golven van haar geactiveerde
   * lijnen (1-2 tribaal, 3-4 individueel, 5-6 collectief) worden uitgelicht.
   */
  protected readonly emotionalWave = computed<EmotionalWaveRowViewModel | null>(() => {
    const gate = this.gate();
    const wave = hdEmotionalWaveForGate(gate);
    if (wave == null) {
      return null;
    }
    return {
      wave,
      keynoteKey: `gates.${gate}.keynote`,
      sourceLines: wave === 'source' ? this.activations().map(activation => activation.line) : [],
    };
  });

  /** Gedefinieerde emotionele kanalen van de chart (badges in het golvenpaneel); buiten een chart onbekend. */
  protected readonly emotionalWaveDefinedChannels = computed<readonly string[] | undefined>(() => {
    const allActivations = this.requestedContext()?.allActivations;
    return allActivations ? hdDefinedEmotionalChannelKeys(allActivations) : undefined;
  });

  /**
   * Sectie-titels van het volledige gate-info panel — verzameld op één plek
   * zodat ze allemaal de actieve content-taal volgen (de transloco-pipe in de
   * template kijkt naar de globale app-taal en zou hier dus de override van
   * de editor-pagina niet honoreren).
   */
  protected readonly planetInfoTooltip = computed(() =>
    this.translateWithLang('humanDesign.gateInfo.openPlanetInfo', this.lang()),
  );

  protected readonly sectionTitles = computed(() => {
    const lang = this.lang();
    const t = (key: string) => this.translateWithLang(`humanDesign.gateInfo.sections.${key}`, lang);
    return {
      planetaryInfluences: t('planetaryInfluences'),
      lineCharacteristics: t('lineCharacteristics'),
      gateDescription: t('gateDescription'),
      evolutionary: t('evolutionary'),
      coordinates: t('coordinates'),
      practicalImplications: t('practicalImplications'),
      reflectionQuestions: t('reflectionQuestions'),
      activations: t('activations'),
    };
  });

  protected readonly categorySections = computed<readonly CategorySectionViewModel[]>(() => {
    const gate = this.gate();
    const lang = this.lang();
    return HD_GATE_CATEGORIES
      .map(category => {
        const entry = this.provider.gateCategoryInfoIn(gate, category, lang);
        if (!entry || !entry.description) {
          return null;
        }
        return { category, description: entry.description };
      })
      .filter((vm): vm is CategorySectionViewModel => vm !== null);
  });

  protected readonly coordinates = computed<CoordinatesViewModel | null>(() => {
    const coords = this.gateInfo().coordinates;
    if (!coords) {
      return null;
    }
    const lang = this.lang();
    return {
      start: formatHdZodiacPosition(coords.start, this.provider.zodiacLabelIn(coords.start.zodiacSign, lang)),
      end: formatHdZodiacPosition(coords.end, this.provider.zodiacLabelIn(coords.end.zodiacSign, lang)),
    };
  });

  private buildHeadline(
    descriptor: HdGateActivationDescriptor,
    planetTranslations: ReturnType<HdPlanetInfoService['planetTranslationsAllIn']>,
    lang: string,
  ): string {
    const sideLabel = descriptor.side
      ? this.translateWithLang(`humanDesign.bodygraphSide.${descriptor.side}`, lang)
      : '';
    const planetLabel = descriptor.planetKey
      ? planetTranslations[descriptor.planetKey]?.name ?? descriptor.planetFallbackLabel ?? ''
      : descriptor.planetFallbackLabel ?? '';
    return [sideLabel, planetLabel].filter(part => part.length > 0).join(' ');
  }

  /**
   * Vertaalt een sleutel in een specifieke taal zonder de actieve app-taal te
   * wijzigen. Valt terug op de sleutel zelf wanneer Transloco geen vertaling
   * terugvindt (dezelfde semantiek als de transloco-pipe).
   */
  private translateWithLang(key: string, lang: string): string {
    return this.transloco.translate<string>(key, undefined, lang);
  }
}
